import { useMemo, useState, useRef } from "react";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { BarAndLineChart } from "../../../../components/charts/BarAndLineChart/BarAndLineChart";
import {
  BarAndLineChartDataProps,
  BarSize,
} from "../../../../components/charts/BarAndLineChart/types";
import { BaseChartColors } from "../../../../components/charts/constants";
import { useGetSpendByHGIndustryData } from "./hooks/useGetSpendByHGIndustryData";
import { useGetSpendByIndustryData } from "./hooks/useGetSpendByIndustryData";
import { mapSpendBreakdownResultToChartData, sortDataBySpend } from "./utils";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { toCustomSpendCategory } from "../../../../utils/workspace/customSpendCategory";
import { determineChartContent, hasNoData } from "../../utils";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { CHART_CONSTANTS } from "../constants";

const { baseId, chartTitle, tooltip } = CHART_CONSTANTS.marketSizeByIndustry;

const generateIndustrySegmentNameForCSV = (segment: string): string => {
  return segment === "hgIndustry" ? "Hg Industry" : "NAICS";
};
interface MarketSizeByIndustryChartProps {
  barSize?: BarSize;
  applyWorkspaceFiltersOnly?: boolean;
}

export function MarketSizeByIndustryChart({
  barSize,
  applyWorkspaceFiltersOnly = false,
}: MarketSizeByIndustryChartProps = {}) {
  const [selectedSegment, setSelectedSegment] = useState("hgIndustry");

  const combinedFilters = useCombinedFilters(applyWorkspaceFiltersOnly);
  const chartRef = useRef<am4charts.XYChart>(null);
  const spendCategoriesWeights = useWorkspaceStore(
    (state) => state.draft?.configuration.tam.spendCategoryWeights
  );
  const spendType = useWorkspaceStore(
    (state) => state.draft?.configuration.tam.spendType
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { loading: naicsLoading, spendByIndustryChartData } =
    useGetSpendByIndustryData({
      companyFilters: combinedFilters || {},
      customSpendCategory: toCustomSpendCategory(
        spendCategoriesWeights,
        spendType
      ),
      localizeInstalls,
    });

  const { loading: hgIndustryLoading, spendByHGIndustryChartData } =
    useGetSpendByHGIndustryData({
      companyFilters: combinedFilters || {},
      customSpendCategory: toCustomSpendCategory(
        spendCategoriesWeights,
        spendType
      ),
      localizeInstalls,
    });

  const top5Industries = useMemo(() => {
    return sortDataBySpend(spendByIndustryChartData).slice(0, 5);
  }, [spendByIndustryChartData]);

  const top5HGIndustries = useMemo(() => {
    return sortDataBySpend(spendByHGIndustryChartData).slice(0, 5);
  }, [spendByHGIndustryChartData]);

  const dataMap = useMemo<{ [key: string]: BarAndLineChartDataProps[] }>(() => {
    return {
      naics: mapSpendBreakdownResultToChartData(top5Industries),
      hgIndustry: mapSpendBreakdownResultToChartData(top5HGIndustries),
    };
  }, [top5HGIndustries, top5Industries]);

  const isLoading = naicsLoading || hgIndustryLoading;

  const noDataReturned = useMemo(
    () => hasNoData(isLoading, dataMap[selectedSegment]),
    [dataMap, selectedSegment, isLoading]
  );

  // Create CSV field mappings with the dynamic segment name
  const csvFieldMappings = useMemo(
    () => ({
      category: generateIndustrySegmentNameForCSV(selectedSegment),
      barSeriesValue: "Spend (USD)",
      lineSeriesValue: "No. Of Companies",
    }),
    [selectedSegment]
  );

  function handleSegmentSelect(event: SelectChangeEvent) {
    setSelectedSegment(event.target.value);
  }

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <BarAndLineChart
          id={`${baseId}-chart`}
          chartData={dataMap[selectedSegment]}
          xAxisLabel="Spend (USD)"
          barSeriesColor={BaseChartColors.Indigo}
          barSeriesName="SPEND (USD)"
          lineSeriesName="NO. OF COMPANIES"
          barSize={barSize}
          ref={chartRef}
        />
      ),
    });
  }, [isLoading, noDataReturned, dataMap, selectedSegment, barSize]);

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      noData={noDataReturned}
      chartRef={chartRef}
      isLoading={isLoading}
      csvFieldMappings={csvFieldMappings}
      titleExtras={
        <FormControl size="small" sx={{ minWidth: 110, maxWidth: "100%" }}>
          <Select
            id={`${baseId}-segment-select`}
            value={selectedSegment}
            onChange={handleSegmentSelect}
          >
            <MenuItem value={"hgIndustry"}>Segment: HG Industry</MenuItem>
            <MenuItem value={"naics"}>Segment: NAICS</MenuItem>
          </Select>
        </FormControl>
      }
    />
  );
}
